import type { DiscountDto, PromotionDto } from '@deli/contracts';
import { useState } from 'react';
import { Link } from 'react-router';
import { DISCOUNT_PAGE, useDiscounts, usePromotions } from '../api/pricing.js';
import { Pager } from '../components/Pager.js';
import { statusBadge } from './CustomersPage.js';
import { QueryState } from '../components/QueryState.js';
import { t } from '../i18n/uk.js';
import {
  DeleteWithReason,
  DiscountDialog,
  PromotionDialog,
  useDeleteDiscountAction,
  useDeletePromotionAction,
} from './PricingDialogs.js';
import {
  formatDiscount,
  formatPeriod,
  PricePreview,
  stateBadge,
  targetLabel,
} from './PricingParts.js';

const tp = t.pricing;

type Dialog =
  | { kind: 'discount'; discount?: DiscountDto }
  | { kind: 'promotion'; promotion?: PromotionDto }
  | null;

/** Pricing (PRC-11, spec §8): discounts, promotions and a sample-cart preview. `pricing.edit`. */
export function PricingPage() {
  const [offset, setOffset] = useState(0);
  const discounts = useDiscounts({ offset });
  const promotions = usePromotions();
  const [dialog, setDialog] = useState<Dialog>(null);
  const deleteDiscount = useDeleteDiscountAction();
  const deletePromotion = useDeletePromotionAction();
  const close = () => setDialog(null);

  return (
    <section className="wide">
      <h1>{tp.title}</h1>
      <p className="muted">{tp.intro}</p>

      <div className="card">
        <div className="page-header">
          <h2>{tp.discountsTitle}</h2>
          <button type="button" className="primary" onClick={() => setDialog({ kind: 'discount' })}>
            {tp.newDiscount}
          </button>
        </div>
        <QueryState query={discounts}>
          {(page) =>
            page.items.length === 0 ? (
              <p className="muted">{tp.noDiscounts}</p>
            ) : (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{tp.customer}</th>
                      <th>{tp.target}</th>
                      <th className="num">{tp.discount}</th>
                      <th>{tp.period}</th>
                      <th>{tp.state}</th>
                      <th>{tp.reason}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {page.items.map((d) => (
                      <tr key={d.id}>
                        <td>
                          <Link to={`/customers/${d.customerId}`}>{d.customerName ?? '—'}</Link>{' '}
                          {d.customerType && (
                            <span className="muted">{t.customers.types[d.customerType]}</span>
                          )}
                          {d.customerStatus &&
                            d.customerStatus !== 'active' &&
                            statusBadge(d.customerStatus)}
                        </td>
                        <td>{targetLabel(d)}</td>
                        <td className="num">{formatDiscount(d.mode, d.value)}</td>
                        <td>{formatPeriod(d.validFrom, d.validTo)}</td>
                        <td>{stateBadge(d.state)}</td>
                        <td className="muted">{d.reason}</td>
                        <td className="actions">
                          <button
                            type="button"
                            onClick={() => setDialog({ kind: 'discount', discount: d })}
                          >
                            {t.common.edit}
                          </button>
                          <DeleteWithReason
                            label={d.customerName ?? d.id}
                            onDelete={deleteDiscount.run(d.id)}
                            pending={deleteDiscount.pending}
                            error={deleteDiscount.error}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pager
                  offset={offset}
                  pageSize={DISCOUNT_PAGE}
                  total={page.total}
                  onChange={setOffset}
                />
              </>
            )
          }
        </QueryState>
      </div>

      <div className="card">
        <div className="page-header">
          <h2>{tp.promotionsTitle}</h2>
          <button
            type="button"
            className="primary"
            onClick={() => setDialog({ kind: 'promotion' })}
          >
            {tp.newPromotion}
          </button>
        </div>
        <QueryState query={promotions}>
          {(list) =>
            list.length === 0 ? (
              <p className="muted">{tp.noPromotions}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{tp.name}</th>
                    <th>{tp.target}</th>
                    <th className="num">{tp.discount}</th>
                    <th>{tp.appliesTo}</th>
                    <th>{tp.period}</th>
                    <th>{tp.state}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{targetLabel(p)}</td>
                      <td className="num">
                        {formatDiscount(p.mode, p.value)}
                        {p.stackable && <div className="muted">{tp.stackableShort}</div>}
                      </td>
                      <td>{p.appliesTo.map((type) => t.customers.types[type]).join(', ')}</td>
                      <td>{formatPeriod(p.validFrom, p.validTo)}</td>
                      <td>{stateBadge(p.state)}</td>
                      <td className="actions">
                        <button
                          type="button"
                          onClick={() => setDialog({ kind: 'promotion', promotion: p })}
                        >
                          {t.common.edit}
                        </button>
                        <DeleteWithReason
                          label={p.name}
                          onDelete={deletePromotion.run(p.id)}
                          pending={deletePromotion.pending}
                          error={deletePromotion.error}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </QueryState>
      </div>

      <div className="card">
        <PricePreview />
      </div>

      {dialog?.kind === 'discount' && <DiscountDialog discount={dialog.discount} onClose={close} />}
      {dialog?.kind === 'promotion' && (
        <PromotionDialog promotion={dialog.promotion} onClose={close} />
      )}
    </section>
  );
}
